import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, message, Empty, Modal, Input } from 'antd';
import api from '../services/api';

const GOLD = '#B8892A';
const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';

const CATEGORIES = ['腕表', '珠宝', '箱包', '配饰', '服装'];
const GRADES = ['S', 'A', 'B', 'C'];

const STATUS_LABEL_KEYS = {
  pending_review: 'merchant.statusPendingReview',
  active: 'merchant.statusActive',
  sold: 'merchant.statusSold',
  returned: 'merchant.statusReturned',
  pending_shipment: 'merchant.statusPendingShipment',
};

export default function MerchantDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview'); // overview | products | settlements | exhibitions | crm
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [myBooths, setMyBooths] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [crm, setCrm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prodLoading, setProdLoading] = useState(false);

  // Add product form
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({
    item_name: '', brand: '', category: '腕表', item_grade: 'A',
    price_cny: '', description: '', pricing_mode: 'fixed',
  });
  const [addPhotos, setAddPhotos] = useState([]);
  const addFileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const excelRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editProd, setEditProd] = useState(null);
  const [editProdForm, setEditProdForm] = useState({ price_cny: '', min_price_cny: '' });
  const [editProdSaving, setEditProdSaving] = useState(false);

  const handleAddPhotoPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (e.target) e.target.value = '';
    const remaining = 9 - addPhotos.length;
    if (remaining <= 0) { message.warning(t('merchant.maxPhotos')); return; }
    const toUpload = files.slice(0, remaining).filter(f => {
      if (f.size > 10 * 1024 * 1024) { message.warning(t('merchant.fileTooLarge', { name: f.name })); return false; }
      return true;
    });
    if (!toUpload.length) return;
    const placeholders = toUpload.map(file => ({
      url: URL.createObjectURL(file), uploading: true, _file: file,
      _id: Math.random().toString(36).slice(2),
    }));
    setAddPhotos(prev => [...prev, ...placeholders]);
    await Promise.all(placeholders.map(async (ph) => {
      const fd = new FormData();
      fd.append('file', ph._file);
      try {
        const res = await api.post('/upload?folder=product', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setAddPhotos(prev => prev.map(p => p._id === ph._id ? { url: res.data.url, uploading: false, _id: ph._id } : p));
      } catch {
        message.error(t('common.failed'));
        setAddPhotos(prev => prev.filter(p => p._id !== ph._id));
      }
    }));
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (e.target) e.target.value = '';
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/merchants/products/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      message.success(t('merchant.importSuccess', { count: res.data.submitted }));
      if (tab === 'products') {
        const pr = await api.get('/merchants/products');
        setProducts(pr.data?.data || []);
      }
    } catch (err) {
      message.error(err.response?.data?.error || t('merchant.importFailed'));
    } finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    window.open(`${api.defaults.baseURL}/merchants/products/template`, '_blank');
  };

  const openEditProd = (p) => {
    setEditProd(p);
    setEditProdForm({ price_cny: p.price_cny || '', min_price_cny: p.min_price_cny || '' });
  };

  const handleEditProdSave = async () => {
    if (!editProdForm.price_cny || Number(editProdForm.price_cny) <= 0) { message.warning(t('merchant.validPrice')); return; }
    setEditProdSaving(true);
    try {
      await api.patch(`/merchants/products/${editProd.id}`, {
        price_cny: Number(editProdForm.price_cny),
        min_price_cny: editProdForm.min_price_cny ? Number(editProdForm.min_price_cny) : null,
      });
      message.success(t('merchant.priceUpdated'));
      setEditProd(null);
      const pr = await api.get('/merchants/products');
      setProducts(pr.data?.data || []);
    } catch (e) { message.error(e.response?.data?.error || t('common.failed')); }
    finally { setEditProdSaving(false); }
  };

  useEffect(() => {
    api.get('/merchants/stats')
      .then(r => setStats(r.data))
      .catch(err => {
        if (err.response?.status === 403) navigate('/merchant/apply');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (tab === 'products') {
      setProdLoading(true);
      api.get('/merchants/products').then(r => setProducts(r.data?.data || [])).catch(() => {}).finally(() => setProdLoading(false));
    }
    if (tab === 'settlements') {
      api.get('/merchants/settlements').then(r => setSettlements(r.data || [])).catch(() => {});
    }
    if (tab === 'exhibitions') {
      api.get('/exhibitions/my-booths').then(r => setMyBooths(r.data || [])).catch(() => {});
      api.get('/exhibitions/my-applications').then(r => setMyApps(r.data || [])).catch(() => {});
    }
    if (tab === 'crm') {
      api.get('/exhibitions/merchant-crm').then(r => setCrm(r.data)).catch(() => {});
    }
  }, [tab]);

  const handleAddProduct = async () => {
    if (!addForm.item_name.trim()) return message.warning(t('merchant.enterProductName'));
    if (!addForm.price_cny || Number(addForm.price_cny) <= 0) return message.warning(t('merchant.validPrice'));

    if (addPhotos.some(p => p.uploading)) { message.warning(t('merchant.photosUploading')); return; }

    setSubmitting(true);
    try {
      const photoUrls = addPhotos.filter(p => !p.uploading).map(p => p.url);
      const payload = { ...addForm, price_cny: Number(addForm.price_cny), photos: photoUrls };
      const r = await api.post('/merchants/products', payload);
      message.success(t('merchant.productSubmitted', { count: r.data.submitted }));
      setShowAdd(false);
      setAddForm({ item_name: '', brand: '', category: '腕表', item_grade: 'A', price_cny: '', description: '', pricing_mode: 'fixed' });
      setAddPhotos([]);
      // Refresh
      if (tab === 'products') {
        const pr = await api.get('/merchants/products');
        setProducts(pr.data?.data || []);
      }
      const sr = await api.get('/merchants/stats');
      setStats(sr.data);
    } catch (err) {
      message.error(err.response?.data?.error || t('common.failed'));
    } finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>{t('merchant.dashboard')}</span>
          <button onClick={() => setShowAdd(true)} style={{
            background: GOLD, border: 'none', borderRadius: 6, padding: '4px 12px',
            color: '#fff', fontSize: 12, cursor: 'pointer',
          }}>+ {t('merchant.submitProduct')}</button>
        </div>

        {/* Stats Banner */}
        {stats && (
          <div style={{
            margin: '16px 16px 0', borderRadius: 14,
            background: 'linear-gradient(135deg, #1A2E28, #2A4A3E)',
            padding: '20px 24px',
          }}>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,.7)', marginBottom: 12 }}>
              {stats.merchant_name} · {t('merchant.commissionLabel')} {stats.commission_rate}%
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {[
                { label: t('merchant.totalSales'), value: `¥${stats.orders.total_sales.toLocaleString()}` },
                { label: t('merchant.totalRevenue'), value: `¥${stats.orders.total_revenue.toLocaleString()}` },
                { label: t('merchant.pendingSettlement'), value: `¥${stats.pending_settlement.toLocaleString()}` },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        {stats && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0,
            margin: '16px 16px 0', background: '#fff', borderRadius: 10,
            border: `1px solid ${BD}`, overflow: 'hidden',
          }}>
            {[
              { label: t('merchant.statusPendingReview'), value: stats.items.pending_review, color: '#E67E22' },
              { label: t('merchant.statusActive'), value: stats.items.active, color: TEAL },
              { label: t('merchant.statusSold'), value: stats.items.sold, color: GOLD },
              { label: t('merchant.totalProducts'), value: stats.items.total, color: INK },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex', margin: '16px 16px 0',
          background: '#fff', borderRadius: 8, border: `1px solid ${BD}`, overflow: 'hidden',
        }}>
          {[
            { key: 'overview', label: t('merchant.tabOverview') },
            { key: 'products', label: t('merchant.tabProducts') },
            { key: 'settlements', label: t('merchant.tabSettlements') },
            { key: 'exhibitions', label: t('merchant.tabExhibitions') },
            { key: 'crm', label: t('merchant.tabCrm') },
          ].map(item => (
            <button key={item.key} onClick={() => setTab(item.key)} style={{
              flex: 1, padding: '12px 0', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: tab === item.key ? 600 : 400,
              color: tab === item.key ? GOLD : MUTED,
              background: tab === item.key ? `${GOLD}08` : 'transparent',
              borderBottom: tab === item.key ? `2px solid ${GOLD}` : '2px solid transparent',
            }}>{item.label}</button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ margin: '16px 16px 0' }}>
          {tab === 'overview' && stats && (
            <div>
              <div style={{
                background: '#fff', borderRadius: 10, border: `1px solid ${BD}`, padding: 16,
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 12 }}>{t('merchant.businessOverview')}</div>
                {[
                  { label: t('merchant.totalOrders'), value: stats.orders.total },
                  { label: t('merchant.completedOrders'), value: stats.orders.completed },
                  { label: t('merchant.commissionRate'), value: `${stats.commission_rate}%` },
                  { label: t('merchant.settlementCycle'), value: stats.settlement_cycle === 'weekly' ? t('merchant.cycleWeekly') : stats.settlement_cycle === 'biweekly' ? t('merchant.cycleBiweekly') : t('merchant.cycleMonthly') },
                ].map(r => (
                  <div key={r.label} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '10px 0',
                    borderBottom: `1px solid ${BD}20`, fontSize: 13,
                  }}>
                    <span style={{ color: MUTED }}>{r.label}</span>
                    <span style={{ color: INK, fontWeight: 500 }}>{r.value}</span>
                  </div>
                ))}
              </div>

              <div style={{
                background: '#fff', borderRadius: 10, border: `1px solid ${BD}`, padding: 16,
                marginTop: 12,
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 12 }}>{t('merchant.productOps')}</div>
                <button onClick={() => setShowAdd(true)} style={{
                  width: '100%', padding: '14px 0', borderRadius: 8, border: `1px dashed ${GOLD}`,
                  background: `${GOLD}06`, color: GOLD, fontSize: 14, cursor: 'pointer',
                }}>+ {t('merchant.submitNewProduct')}</button>
                <input ref={excelRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleExcelImport} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => excelRef.current?.click()} disabled={importing} style={{
                    flex: 1, padding: '12px 0', borderRadius: 8, border: `1px solid ${BD}`,
                    background: '#fff', color: importing ? '#ccc' : INK, fontSize: 13, cursor: 'pointer',
                  }}>{importing ? t('merchant.importing') : t('merchant.excelImport')}</button>
                  <button onClick={downloadTemplate} style={{
                    padding: '12px 14px', borderRadius: 8, border: `1px solid ${BD}`,
                    background: '#fff', color: MUTED, fontSize: 13, cursor: 'pointer',
                  }}>{t('merchant.downloadTemplate')}</button>
                </div>
                {importResult && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: '#F0FAF4', borderRadius: 6, fontSize: 12 }}>
                    <span style={{ color: TEAL, fontWeight: 600 }}>{t('merchant.imported', { count: importResult.submitted })}</span>
                    {importResult.errors?.length > 0 && (
                      <div style={{ color: '#C0392B', marginTop: 4 }}>
                        {importResult.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
                        {importResult.errors.length > 5 && <div>...{t('merchant.moreErrors', { count: importResult.errors.length - 5 })}</div>}
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => setTab('products')} style={{
                  width: '100%', padding: '12px 0', borderRadius: 8, border: `1px solid ${BD}`,
                  background: '#fff', color: MUTED, fontSize: 13, cursor: 'pointer', marginTop: 8,
                }}>{t('merchant.viewAllProducts')} ›</button>
              </div>
            </div>
          )}

          {tab === 'products' && (
            prodLoading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> :
            products.length === 0 ? <Empty description={t('common.noData')} /> :
            <div>
              {products.map(p => {
                const photos = p.photos || [];
                const thumb = photos[0] || '';
                return (
                  <div key={p.id} style={{
                    display: 'flex', gap: 12, padding: 14,
                    background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                    marginBottom: 10,
                  }}>
                    <div style={{
                      width: 72, height: 72, borderRadius: 8, flexShrink: 0,
                      background: thumb ? `url(${thumb}) center/cover` : '#F5F5F5',
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 14, fontWeight: 500, color: INK,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{p.item_name}</div>
                      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                        {p.brand} · {p.item_no}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: INK }}>
                          ¥{Number(p.price_cny || 0).toLocaleString()}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {!['sold', 'cancelled'].includes(p.status) && (
                            <button onClick={() => openEditProd(p)} style={{
                              padding: '2px 8px', borderRadius: 4, border: `1px solid ${GOLD}`,
                              background: 'transparent', color: GOLD, fontSize: 11, cursor: 'pointer',
                            }}>{t('merchant.adjustPrice')}</button>
                          )}
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 4,
                            background: p.status === 'active' ? `${TEAL}15` : `${GOLD}15`,
                            color: p.status === 'active' ? TEAL : GOLD,
                          }}>{STATUS_LABEL_KEYS[p.status] ? t(STATUS_LABEL_KEYS[p.status]) : p.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'settlements' && (
            settlements.length === 0 ? (
              <div style={{
                background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                padding: '40px 16px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 14, color: MUTED }}>{t('merchant.noSettlements')}</div>
                <div style={{ fontSize: 12, color: '#CCC', marginTop: 4 }}>{t('merchant.settlementDesc')}</div>
              </div>
            ) : (
              <div>
                {settlements.map(s => (
                  <div key={s.id} style={{
                    background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                    padding: 16, marginBottom: 10,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>
                        {s.period_start} ~ {s.period_end}
                      </span>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 4,
                        background: s.status === 'paid' ? `${TEAL}15` : `${GOLD}15`,
                        color: s.status === 'paid' ? TEAL : GOLD,
                      }}>{s.status === 'paid' ? t('merchant.settlementPaid') : s.status === 'confirmed' ? t('merchant.settlementConfirmed') : t('merchant.settlementPending')}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 12 }}>
                      <div><span style={{ color: MUTED }}>{t('merchant.salesAmount')} </span><span style={{ fontWeight: 500 }}>¥{Number(s.total_sales).toLocaleString()}</span></div>
                      <div><span style={{ color: MUTED }}>{t('merchant.commissionLabel')} </span><span style={{ fontWeight: 500 }}>¥{Number(s.total_commission).toLocaleString()}</span></div>
                      <div><span style={{ color: MUTED }}>{t('merchant.settlement')} </span><span style={{ fontWeight: 600, color: TEAL }}>¥{Number(s.net_amount).toLocaleString()}</span></div>
                    </div>
                    <div style={{ fontSize: 11, color: '#CCC', marginTop: 6 }}>{s.order_count} {t('merchant.orderCount')}</div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === 'exhibitions' && (
            <div>
              {/* My Booths */}
              <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>{t('merchant.myBooths')}</div>
              {myBooths.length === 0 ? (
                <div style={{
                  background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                  padding: '30px 16px', textAlign: 'center', color: MUTED, fontSize: 13, marginBottom: 16,
                }}>{t('merchant.noBooths')}</div>
              ) : (
                <div style={{ marginBottom: 16 }}>
                  {myBooths.map(b => (
                    <div key={b.id} style={{
                      background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                      padding: 16, marginBottom: 10,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>
                          {b.exhibition_name || t('exhibition.title')} — {b.booth_no}
                        </span>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 4,
                          background: `${TEAL}15`, color: TEAL,
                        }}>{b.zone || t('merchant.standardZone')}</span>
                      </div>
                      <div style={{ fontSize: 12, color: MUTED }}>
                        {b.size_type === 'premium' ? t('merchant.boothPremium') : b.size_type === 'island' ? t('merchant.boothIsland') : t('merchant.boothStandard')}
                        {b.live_url && <span style={{ marginLeft: 8, color: '#E74C3C' }}>● {t('live.streaming')}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* My Applications */}
              <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>{t('merchant.applicationRecords')}</div>
              {myApps.length === 0 ? (
                <div style={{
                  background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                  padding: '30px 16px', textAlign: 'center', color: MUTED, fontSize: 13, marginBottom: 16,
                }}>{t('merchant.noApplications')}</div>
              ) : (
                <div style={{ marginBottom: 16 }}>
                  {myApps.map(a => {
                    const statusMap = { pending: t('merchant.appStatusPending'), approved: t('merchant.appStatusApproved'), rejected: t('merchant.appStatusRejected') };
                    const colorMap = { pending: '#E67E22', approved: TEAL, rejected: '#E74C3C' };
                    return (
                      <div key={a.id} style={{
                        background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                        padding: 16, marginBottom: 10,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontSize: 14, fontWeight: 500, color: INK }}>{a.exhibition_name}</span>
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 4,
                            background: `${colorMap[a.status]}15`, color: colorMap[a.status],
                          }}>{statusMap[a.status] || a.status}</span>
                        </div>
                        <div style={{ fontSize: 12, color: MUTED }}>
                          {t('merchant.preference')}: {a.preferred_size === 'premium' ? t('merchant.boothPremium') : a.preferred_size === 'island' ? t('merchant.boothIsland') : t('merchant.boothStandard')}
                          {a.preferred_zone && ` · ${a.preferred_zone}`}
                        </div>
                        {a.reject_reason && (
                          <div style={{ fontSize: 12, color: '#E74C3C', marginTop: 4 }}>{t('merchant.rejectReason')}: {a.reject_reason}</div>
                        )}
                        {a.booth_no && (
                          <div style={{ fontSize: 12, color: TEAL, marginTop: 4 }}>{t('merchant.assignedBooth')}: {a.booth_no}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Browse exhibitions link */}
              <button onClick={() => navigate('/exhibitions')} style={{
                width: '100%', padding: '14px 0', borderRadius: 8, border: `1px solid ${BD}`,
                background: '#fff', color: TEAL, fontSize: 14, fontWeight: 500, cursor: 'pointer',
              }}>{t('merchant.browseExhibitions')} ›</button>
            </div>
          )}

          {tab === 'crm' && (
            !crm ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : (
              <div>
                {/* Credit Score Summary */}
                {crm.credit && (
                  <div style={{
                    background: 'linear-gradient(135deg, #1A2E28, #2A4A3E)',
                    borderRadius: 10, padding: 16, marginBottom: 12, color: '#fff',
                  }}>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', marginBottom: 8 }}>{t('merchant.myCreditScore')}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <span style={{ fontSize: 36, fontWeight: 700 }}>{crm.credit.total_score}</span>
                      <span style={{
                        padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: crm.credit.tier === 'premium' ? '#FFF8E1' : crm.credit.tier === 'excellent' ? 'rgba(255,255,255,.15)' : 'rgba(255,255,255,.1)',
                        color: crm.credit.tier === 'premium' ? GOLD : '#fff',
                      }}>{crm.credit.tier === 'premium' ? t('merchant.creditPlatinum') : crm.credit.tier === 'excellent' ? t('merchant.creditExcellent') : crm.credit.tier === 'good' ? t('merchant.creditGood') : t('merchant.creditNormal')}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 11 }}>
                      <div><span style={{ color: 'rgba(255,255,255,.5)' }}>{t('merchant.scoreTrade')} </span><span>{crm.credit.trade_score}</span></div>
                      <div><span style={{ color: 'rgba(255,255,255,.5)' }}>{t('merchant.scoreQuality')} </span><span>{crm.credit.quality_score}</span></div>
                      <div><span style={{ color: 'rgba(255,255,255,.5)' }}>{t('merchant.scoreService')} </span><span>{crm.credit.service_score}</span></div>
                    </div>
                  </div>
                )}

                {/* View Stats */}
                <div style={{
                  background: '#fff', borderRadius: 10, border: `1px solid ${BD}`, padding: 16, marginBottom: 12,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>{t('merchant.boothTraffic')}</div>
                  {crm.view_stats.length === 0 ? (
                    <div style={{ fontSize: 13, color: MUTED, padding: '12px 0' }}>{t('merchant.noViewData')}</div>
                  ) : crm.view_stats.map(v => (
                    <div key={v.booth_id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 0', borderBottom: `1px solid ${BD}20`,
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: INK }}>{v.exhibition_name}</div>
                        <div style={{ fontSize: 11, color: MUTED }}>{t('merchant.booth')} {v.booth_no}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: TEAL }}>{v.total_views}</div>
                        <div style={{ fontSize: 10, color: MUTED }}>{v.unique_visitors} {t('merchant.uniqueVisitors')} · {v.active_days} {t('merchant.activeDays')}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Favorite Stats */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12,
                }}>
                  <div style={{
                    background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                    padding: 16, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: GOLD }}>{crm.favorite_stats.total_favorites}</div>
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{t('merchant.favoriteCount')}</div>
                  </div>
                  <div style={{
                    background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                    padding: 16, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: TEAL }}>{crm.favorite_stats.unique_fans}</div>
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{t('merchant.fansCount')}</div>
                  </div>
                </div>

                {/* Appointments */}
                <div style={{
                  background: '#fff', borderRadius: 10, border: `1px solid ${BD}`, padding: 16,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>{t('merchant.appointmentRecords')}</div>
                  {crm.appointments.length === 0 ? (
                    <div style={{ fontSize: 13, color: MUTED, padding: '12px 0' }}>{t('merchant.noAppointments')}</div>
                  ) : crm.appointments.map(a => (
                    <div key={a.id} style={{
                      padding: '10px 0', borderBottom: `1px solid ${BD}20`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ fontWeight: 500, color: INK }}>
                          {a.customer_name || t('merchant.customer')} {a.customer_phone && <span style={{ color: MUTED, fontSize: 11 }}>{a.customer_phone}</span>}
                        </span>
                        <span style={{
                          fontSize: 11, padding: '1px 6px', borderRadius: 3,
                          background: a.status === 'confirmed' ? `${TEAL}15` : `${GOLD}15`,
                          color: a.status === 'confirmed' ? TEAL : GOLD,
                        }}>{a.status === 'confirmed' ? t('merchant.appointConfirmed') : a.status === 'completed' ? t('merchant.appointCompleted') : t('merchant.appointPending')}</span>
                      </div>
                      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                        {a.exhibition_name} · {a.booth_no} · {new Date(a.appointment_time).toLocaleString('zh-CN')}
                      </div>
                      {a.message && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{a.message}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>

        {/* Add Product Modal */}
        <Modal
          open={showAdd}
          onCancel={() => setShowAdd(false)}
          onOk={handleAddProduct}
          confirmLoading={submitting}
          title={t('merchant.products')}
          okText={t('common.submit')}
          cancelText={t('common.cancel')}
          width={500}
        >
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('consignment.itemName')} *</label>
            <Input value={addForm.item_name} onChange={e => setAddForm({ ...addForm, item_name: e.target.value })}
              placeholder="如：Rolex Submariner Date 126610LN" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('consignment.brand')}</label>
              <Input value={addForm.brand} onChange={e => setAddForm({ ...addForm, brand: e.target.value })}
                placeholder="Rolex" />
            </div>
            <div>
              <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('consignment.category')}</label>
              <select value={addForm.category} onChange={e => setAddForm({ ...addForm, category: e.target.value })}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${BD}` }}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('browse.grade')}</label>
              <select value={addForm.item_grade} onChange={e => setAddForm({ ...addForm, item_grade: e.target.value })}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${BD}` }}>
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('bill.amount')} (CNY) *</label>
              <Input value={addForm.price_cny} onChange={e => setAddForm({ ...addForm, price_cny: e.target.value })}
                placeholder="28000" type="number" />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('consignment.description')}</label>
            <Input.TextArea value={addForm.description} onChange={e => setAddForm({ ...addForm, description: e.target.value })}
              placeholder={t('consignment.description')} rows={3} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('consignment.photos')}</label>
            <input ref={addFileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAddPhotoPick} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {addPhotos.map((p, i) => (
                <div key={i} style={{ width: 64, height: 64, borderRadius: 4, overflow: 'hidden', position: 'relative', border: `1px solid ${BD}` }}>
                  <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: p.uploading ? 0.4 : 1 }} />
                  {p.uploading && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: MUTED }}>...</div>}
                  {!p.uploading && <button onClick={() => setAddPhotos(prev => prev.filter((_, j) => j !== i))} style={{ position: 'absolute', top: 1, right: 1, width: 16, height: 16, borderRadius: '50%', background: 'rgba(0,0,0,.5)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 10, lineHeight: '16px', padding: 0 }}>×</button>}
                </div>
              ))}
              {addPhotos.length < 9 && (
                <div onClick={() => addFileRef.current?.click()} style={{ width: 64, height: 64, borderRadius: 4, border: `2px dashed ${BD}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fff' }}>
                  <span style={{ fontSize: 18, color: MUTED }}>+</span>
                  <span style={{ fontSize: 9, color: MUTED }}>{addPhotos.length}/9</span>
                </div>
              )}
            </div>
          </div>
          <div style={{ fontSize: 12, color: MUTED, padding: '8px 0', borderTop: `1px solid ${BD}` }}>
            提交后商品将进入「待审核」状态，平台审核通过后自动上架。
          </div>
        </Modal>

        {/* Edit Price Modal */}
        <Modal open={!!editProd} onCancel={() => setEditProd(null)} title={t('common.edit')} width={400}
          onOk={handleEditProdSave} confirmLoading={editProdSaving} okText={t('common.save')} cancelText={t('common.cancel')}>
          {editProd && (
            <div>
              <div style={{ fontSize: 13, color: MUTED, marginBottom: 12 }}>
                {editProd.item_name}（{editProd.item_no}）
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('bill.amount')} *</label>
                <Input type="number" value={editProdForm.price_cny}
                  onChange={e => setEditProdForm({ ...editProdForm, price_cny: e.target.value })} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('common.optional')}</label>
                <Input type="number" value={editProdForm.min_price_cny}
                  onChange={e => setEditProdForm({ ...editProdForm, min_price_cny: e.target.value })}
                  placeholder={t('common.optional')} />
              </div>
              <div style={{ fontSize: 12, color: MUTED, padding: '6px 0', borderTop: `1px solid ${BD}` }}>
                调价后将立即生效，已上架商品的展示价格会同步更新。
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
